import { contactKeys, entryKeys, ledgerKey, type LentToTarget } from '@taler/api-client'
import type { Money } from '@taler/domain'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { problemDetail } from '../internal/problem.ts'

export interface ForgiveInput {
  readonly target: LentToTarget
  readonly amount: Money
  /** Contact only - omitted defaults to the ledger's own Gifts account. */
  readonly expenseAccountId?: string
}

export interface UseForgive {
  readonly isSubmitting: ComputedRef<boolean>
  readonly hasError: ComputedRef<boolean>
  readonly errorDetail: ComputedRef<string | undefined>
  submit(input: ForgiveInput): Promise<void>
  reset(): void
}

/** Final and irreversible; invalidates every reporting read so the change is visible with no reload. */
export function useForgive(ledgerId: () => string): UseForgive {
  const api = useApi()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (input: ForgiveInput) => {
      if (input.target.kind === 'contact') {
        await api.contacts.forgive(ledgerId(), input.target.contactId, {
          amount: input.amount,
          ...(input.expenseAccountId === undefined ? {} : { expenseAccountId: input.expenseAccountId }),
        })
      } else {
        await api.crossLedgerDebts.forgive(ledgerId(), input.target.otherLedgerId, { amount: input.amount })
      }
    },
    onSuccess: async (_data, input) => {
      const id = ledgerId()
      const invalidations = [queryClient.invalidateQueries({ queryKey: ledgerKey(id, 'reporting') })]
      if (input.target.kind === 'contact') {
        invalidations.push(
          ...entryKeys.invalidatedByWrite(id).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
          queryClient.invalidateQueries({ queryKey: contactKeys.balances(id, input.target.contactId) }),
        )
      }
      await Promise.all(invalidations)
    },
  })

  return {
    isSubmitting: computed(() => mutation.isPending.value),
    hasError: computed(() => mutation.error.value !== null),
    errorDetail: computed(() => problemDetail(mutation.error.value)),
    submit: (input) => mutation.mutateAsync(input).then(() => undefined),
    reset: () => mutation.reset(),
  }
}
